import React, { useState, useEffect, useContext, useRef } from "react";
import { AddIcon, CancelIcon, DeleteIcon, EditIcon, ICON_SIZE, SaveIcon } from "./actionIcons";
import { Theme, Button, Flex } from "@radix-ui/themes";
import * as Dialog from "@radix-ui/react-dialog";
import fields from "./fields";
import { checkFieldValue, normalizeFieldValue } from "./validation/fieldCheck";
import { contactFieldFor } from "./validation/contactFieldFor";
import { GlobalContext } from "./GlobalContext";
import RecipientMessageHistory from "./RecipientMessageHistory";
import utilities, { formatPhoneForDisplay, formatPhoneForStorage, PHONE_DISPLAY_FIELDS } from "./UtilityFunctions";
import { Tabs, TabList, Tab, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import { useToast } from "./ToastContext";
import RadixModalHeader from "./RadixModalHeader";
import { Store, AlertTriangle, Copy } from "lucide-react";

import confirmDelete from "./confirmDelete";
import { apiFetch } from './api.js';

const FormModeCtx = React.createContext("add");

function AddEditSeller({ formMode = "add", sellerData, SellerID, showModal, parentSheetName = "Sellers" }) {
  const globals = useContext(GlobalContext);
  const { socket, toggleMessageBox, getMsgBoxResult, can } = globals;
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(formMode === "fetch" && !!SellerID);
  const [fetchError, setFetchError] = useState("");
  const [activeTab, setActiveTab] = useState(0); // 0 for Seller Details, 1 for Purchase History
  const [purchaseHistory, setPurchaseHistory] = useState(null);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");

  // Duplicate checking states
  const [duplicates, setDuplicates] = useState([]);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [pendingSubmission, setPendingSubmission] = useState(false);
  const [lastCheckedData, setLastCheckedData] = useState({});

  const [formData, setFormData] = useState(() => {
    if (formMode === "add") {
      return {
        IsCompany: false,
        CompanyName: "",
        FirstName: "",
        LastName: "",
        IDType: fields.IDType.list[0],
        IDNumber: "",
        Address: "",
        Phone1: "",
        Phone2: "",
      };
    } else if (formMode === "fetch") {
      return {};
    } else {
      return sellerData;
    }
  });
  const [backupFormData, setBackupFormData] = useState(formData)
  const table_name = "Sellers";
  const primary_key = "SellerID";
  const { sendOrFail } = utilities;

  // Format single record function
  const formatRecord = (record, fieldsDefinition) => {
    const newRecord = { ...record }; //If it's not a new object, state doesn't update
    Object.keys(newRecord).forEach((field) => {
      if (fieldsDefinition[field]) {
        newRecord[field] = utilities.formatFromInput(newRecord[field], fieldsDefinition[field].dataType);
      }
      if (field === "Phone1" || field === "Phone2") {
        newRecord[field] = formatPhoneForDisplay(newRecord[field]);
      }
    });
    return newRecord;
  };

  const pendingSubmissionRef = useRef(pendingSubmission);
  useEffect(() => {
    pendingSubmissionRef.current = pendingSubmission;
  }, [pendingSubmission]);

  const showDuplicateDialogRef = useRef(showDuplicateDialog);
  useEffect(() => {
    showDuplicateDialogRef.current = showDuplicateDialog;
  }, [showDuplicateDialog]);

  // Check for duplicates function
  const checkForDuplicates = () => {
    const { FirstName, LastName, CompanyName, IsCompany } = formData;

    if ((!FirstName && !LastName) && (!CompanyName && !IsCompany)) {
      return;
    }

    const currentCheckData = { FirstName, LastName, CompanyName, IsCompany };
    if (JSON.stringify(currentCheckData) === JSON.stringify(lastCheckedData)) {
      setPendingSubmission(false);
      return;
    }

    setLastCheckedData(currentCheckData);

    if (socket && socket.connected) {
      const payload = {
        DealershipID: globals.DealershipID,
        FirstName: FirstName?.trim(),
        LastName: LastName?.trim(),
        CompanyName: CompanyName?.trim(),
        IsCompany: IsCompany,
        excludeSellerID: formMode === "edit" ? SellerID : null
      };
      socket.emit('check-duplicate-seller', payload);
    }
  };

  // Fetch seller data when in fetch mode
  useEffect(() => {
    if (formMode === "fetch" && SellerID) {
      console.log("Fetching seller data for SellerID:", SellerID);
      setIsLoading(true);
      setFetchError("");
      apiFetch("/getoneseller", { body: {
          DealershipID: globals.DealershipID,
          SellerID: SellerID
        } })
      .then(async (res) => {
        const resObj = await res.json();
        if (res.ok) {
          const formattedRecord = formatRecord(resObj.data, fields);
          console.log("Fetched and formatted seller data:", formattedRecord);
          setFormData(formattedRecord);
          setBackupFormData(formattedRecord);
        } else {
          setFetchError(resObj.message || resObj.error || "Failed to fetch seller data");
        }
      })
      .catch((err) => {
        setFetchError("Network error occurred while fetching seller data");
        console.error("Fetch error:", err);
      })
      .finally(() => {
        setIsLoading(false);
      });
    }
  }, [formMode, SellerID, globals.DealershipID]);

  // Fetch purchase history when the tab is switched
  useEffect(() => {
    if (activeTab === 1 && SellerID && !purchaseHistory && !isHistoryLoading) {
      setIsHistoryLoading(true);
      setHistoryError("");
      apiFetch("/getsellerhistory", { body: {
          DealershipID: globals.DealershipID,
          SellerID: SellerID
        } })
      .then(async (res) => {
        const resObj = await res.json();
        if (res.ok) {
          setPurchaseHistory(resObj.data);
        } else {
          setHistoryError(resObj.message || resObj.error || "Failed to fetch purchase history");
        }
      })
      .catch((err) => {
        setHistoryError("Network error occurred while fetching purchase history");
        console.error("Fetch error:", err);
      })
      .finally(() => {
        setIsHistoryLoading(false);
      });
    }
  }, [activeTab, SellerID]);

  // Duplicate check listeners
  const duplicateResultsListener = (msg) => {
    if (msg.success && msg.duplicates.length > 0) {
      setDuplicates(msg.duplicates);
      if (pendingSubmissionRef.current) {
        setShowDuplicateDialog(true);
      }
    } else if (pendingSubmissionRef.current) {
      setPendingSubmission(false);
      proceedWithSubmission();
    }
  };

  const duplicateErrorListener = (msg) => {
    if (pendingSubmissionRef.current) {
      toggleMessageBox({
        on: true,
        title: "Duplicate Check Error",
        message: "Unable to check for duplicate sellers. Do you want to continue anyway?",
        buttons: ["Continue", "Cancel"],
        clicked: "",
        onClose: (button) => {
          if (button === "Continue") {
            setPendingSubmission(false);
            proceedWithSubmission();
          } else {
            setPendingSubmission(false);
          }
        }
      });
    }
  };

  const formDataRef = useRef(formData);
  const backupFormDataRef = useRef(backupFormData);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    backupFormDataRef.current = backupFormData;
  }, [backupFormData]);

  const fieldUpdateListener = (msg) => {
    if (msg.sheetName !== parentSheetName) {
      return;
    }
    if (msg.ok) {
      setBackupFormData({ ...backupFormDataRef.current, [msg.fieldName]: formDataRef.current[msg.fieldName] });
      showToast('success', 'Field saved', 2000);
    } else {
      setFormData({ ...formDataRef.current, [msg.fieldName]: backupFormDataRef.current[msg.fieldName] });
      showToast('error', 'Failed to save. Change reverted.');
    }
  }


  useEffect(() => {
    if (socket.disconnected) {
      socket.connect()
    }

    socket.on('update_record', fieldUpdateListener);
    socket.on('duplicate-seller-results', duplicateResultsListener);
    socket.on('duplicate-seller-error', duplicateErrorListener);

    return () => {
      socket.off('update_record', fieldUpdateListener);
      socket.off('duplicate-seller-results', duplicateResultsListener);
      socket.off('duplicate-seller-error', duplicateErrorListener);
    }
  }, []);

  const [errors, setErrors] = useState({
    IsCompany: "",
    CompanyName: "",
    FirstName: "",
    LastName: "",
    IDType: "",
    IDNumber: "",
    Address: "",
    Phone1: "",
    Phone2: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleBlur = ({ target }) => {
    let err = "";
    const attr = target.name;
    //One shared checker covers required/length/pattern, and treats an empty
    //value as its own case rather than pattern-testing a raw null.
    //
    //Canonicalise FIRST, so the value judged is the value stored — and so the
    //user watches "+256 772 123 456" become "0772123456" rather than being told
    //off for pasting the number exactly as their contacts app hands it over.
    //On blur, never on change: rewriting mid-keystroke would make the + vanish
    //under the cursor while they were still typing.
    //
    //The rule comes from contactFieldFor(table_name, ...) and not fields[attr],
    //because Sellers.Phone1 and Customers.Phone1 are the same column under
    //different rules — a seller may be Kenyan or Tanzanian, a customer may not.
    const meta = contactFieldFor(table_name, attr);
    const normalized = normalizeFieldValue(formData[attr], meta);
    if (normalized !== formData[attr]) {
      setFormData(prev => ({ ...prev, [attr]: normalized }));
    }
    const failure = checkFieldValue(normalized, meta);
    if (failure) {
      err = failure.message;
    }

    setErrors(prev => {
      return { ...prev, [attr]: err }
    })

    if (err) {
      target.focus();
      return;
    }

    if (formMode === "edit" && !(normalized === backupFormData[attr])) {
      //`normalized`, not target.value. target.value is the raw DOM string from
      //before canonicalisation, so a pasted "+256772123456" would reach the
      //socket while the field on screen read "0772123456" — and the audit trail
      //would record the wrong @new_value. formatPhoneForStorage only rewrites a
      //leading 0, so it would not have rescued it either.
      const valueToSend = PHONE_DISPLAY_FIELDS.includes(attr)
        ? formatPhoneForStorage(normalized)
        : normalized;
      sendOrFail({
        DealershipID: globals.DealershipID,
        UserID: globals.userData.UserID,
        ownSheetName: parentSheetName,
        table: table_name,
        fieldName: target.name,
        fieldValue: valueToSend,
        primaryKeyName: primary_key,
        primaryKeyValue: formData.SellerID
      }, socket, formData, backupFormData, setFormData)
    }
  }

  const validate = () => {
    let tempErrors = {};
    //Normalise here too, not just on blur: Enter submits the form, and Save can
    //be clicked straight from a focused input, so the last field edited may
    //never have blurred. Judging its raw value would reject a number the app
    //would have accepted a keystroke later.
    const normalizedForm = { ...formData };
    for (let attr of Object.keys(formData)) {
      const meta = contactFieldFor(table_name, attr);
      normalizedForm[attr] = normalizeFieldValue(formData[attr], meta);
      const failure = checkFieldValue(normalizedForm[attr], meta);
      if (failure) {
        tempErrors[attr] = failure.message;
      }
    }
    setFormData(normalizedForm);
    if (formData.IsCompany && formData.CompanyName === "") {
      tempErrors.CompanyName = "Company name is required";
    }
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const proceedWithSubmission = () => {
    apiFetch("/addseller", { body: {
        ...formDataRef.current,
        //Canonicalise at the send boundary rather than trusting that validate()'s
        //setFormData has already reached formDataRef — it is written by an effect
        //on formData, and the add path submits from inside a 50ms setTimeout. The
        //server normalises again regardless; this keeps the request itself honest
        //(and the duplicate check comparing the same shape the DB holds).
        Phone1: formatPhoneForStorage(normalizeFieldValue(formDataRef.current.Phone1, contactFieldFor(table_name, "Phone1"))),
        Phone2: formatPhoneForStorage(normalizeFieldValue(formDataRef.current.Phone2, contactFieldFor(table_name, "Phone2"))),
        DealershipID: globals.DealershipID,
        UserID: globals.userData.UserID,
        ownSheetName: parentSheetName
      } })
    .then(async (res) => {
      const resObj = await res.json();
      if (res.ok) {
        showToast('success', 'Seller added');
        showModal({
          visible: false,
          formMode: ""
        })
      } else {
        toggleMessageBox({
          on: true,
          title: "Fetch Error",
          message: resObj.message,
          buttons: ["OK"],
          clicked: ""
        })
      }
    }).catch((err) => {
      toggleMessageBox({
        on: true,
        title: "Network Error",
        message: err.message,
        buttons: ["OK"],
        clicked: ""
      })
    })
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      if (formMode === "add") {
        setPendingSubmission(true);
        setTimeout(() => {
          checkForDuplicates();
        }, 50);
      } else {
        showModal({
          visible: false,
          formMode: ""
        })
      }
    }
  };

  const handleClose = () => {
    showModal({
      visible: false,
      formMode: ""
    })
  };

  const handleDeleteSeller = async () => {
    const name = formData.IsCompany
      ? (formData.CompanyName || "")
      : `${formData.FirstName || ""} ${formData.LastName || ""}`.trim();
    const ok = await confirmDelete(
      { getMsgBoxResult },
      {
        title: "Delete seller?",
        message: `Delete ${name}? It can be restored from the recycle bin for 90 days.`,
      }
    );
    if (!ok) return;
    try {
      const res = await apiFetch(`/sellers/${formData.SellerID}`, { method: "DELETE" });
      const body = await res.json();
      if (res.ok) {
        handleClose();
      } else {
        toggleMessageBox({
          on: true,
          title: res.status === 409 ? "Can't delete" : "Delete failed",
          message: body.message || "An error occurred.",
          buttons: ["OK"],
        });
      }
    } catch (err) {
      toggleMessageBox({
        on: true,
        title: "Delete failed",
        message: err.message,
        buttons: ["OK"],
      });
    }
  };

  const toggleCheckbox = () => {
    setFormData(prev => {
      const newData = { ...prev, IsCompany: !prev.IsCompany };
      setTimeout(() => {
        if (formMode === "add") {
          checkForDuplicates();
        }
      }, 100);
      return newData;
    })
  }

  const handleDuplicateDialogClose = (proceed) => {
    setShowDuplicateDialog(false);
    setPendingSubmission(false);
    if (proceed) {
      proceedWithSubmission();
    }
  };

  const getModalTitle = () => {
    switch (formMode) {
      case "add": return "New Seller";
      case "edit": return "Seller";
      case "fetch": return "Seller Details";
      default: return "Seller";
    }
  };

  const formatSellerDisplay = (seller) => {
    if (seller.IsCompany) {
      return seller.CompanyName;
    } else {
      return `${seller.FirstName} ${seller.LastName}`.trim();
    }
  };

  // Viewing with nothing selected — the picker allows a null seller, so show
  // a friendly empty state instead of fetching (nothing would resolve the spinner)
  if (formMode === "fetch" && !SellerID) {
    return (
      <Dialog.Root open={true} onOpenChange={() => showModal({ visible: false })} container={document.body}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-40" />
          <Theme>
            <Dialog.Content
              className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-[var(--theme-radius-lg)] w-full max-w-lg"
              style={{ transform: 'translate(-50%, -50%)', backfaceVisibility: 'hidden', background: 'var(--theme-surface)', boxShadow: 'var(--theme-shadow-dialog)', padding: 0 }}
              onPointerDownOutside={(e) => e.preventDefault()}
              onEscapeKeyDown={(e) => e.preventDefault()}
            >
              <RadixModalHeader dialog={Dialog} icon={Store} title="Seller Details" onClose={handleClose} />
              <div className="flex flex-col items-center justify-center py-8 px-6">
                <p className="mb-4" style={{ color: 'var(--theme-muted)', fontSize: '0.9375rem' }}>
                  No seller is selected for this record.
                </p>
                <Button
                  variant="soft"
                  color="gray"
                  radius="medium"
                  onClick={handleClose}
                >
                  <CancelIcon size={ICON_SIZE} />
                  Close
                </Button>
              </div>
            </Dialog.Content>
          </Theme>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <Dialog.Root open={true} onOpenChange={() => showModal({ visible: false })} container={document.body}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-40" />
          <Theme>
            <Dialog.Content
              className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 p-6 rounded-[var(--theme-radius-lg)] w-full max-w-lg"
              style={{ transform: 'translate(-50%, -50%)', backfaceVisibility: 'hidden', background: 'var(--theme-surface)', boxShadow: 'var(--theme-shadow-dialog)' }}
              onPointerDownOutside={(e) => e.preventDefault()}
              onEscapeKeyDown={(e) => e.preventDefault()}
            >
              <div className="flex flex-col items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 mb-4" style={{ borderBottom: '2px solid var(--theme-accent)' }}></div>
                <p style={{ color: 'var(--theme-muted)' }}>Loading seller data...</p>
              </div>
            </Dialog.Content>
          </Theme>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }

  // Show error state
  if (fetchError && formMode === "fetch") {
    return (
      <Dialog.Root open={true} onOpenChange={() => showModal({ visible: false })} container={document.body}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-40" />
          <Theme>
            <Dialog.Content
              className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-[var(--theme-radius-lg)] w-full max-w-lg"
              style={{ transform: 'translate(-50%, -50%)', backfaceVisibility: 'hidden', background: 'var(--theme-surface)', boxShadow: 'var(--theme-shadow-dialog)', padding: 0 }}
              onPointerDownOutside={(e) => e.preventDefault()}
              onEscapeKeyDown={(e) => e.preventDefault()}
            >
              <RadixModalHeader dialog={Dialog} icon={AlertTriangle} title="Error Loading Seller" variant="danger" showClose={false} />
              <div className="flex flex-col items-center justify-center py-8 px-6">
                <p className="error-t mb-4" style={{ fontSize: '0.9375rem' }}>{fetchError}</p>
                <Button
                  variant="soft"
                  color="gray"
                  radius="medium"
                  onClick={handleClose}
                >
                  <CancelIcon size={ICON_SIZE} />
                  Close
                </Button>
              </div>
            </Dialog.Content>
          </Theme>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }

  const renderPurchaseHistory = () => {
    if (isHistoryLoading) {
      return (
        <div className="flex justify-center items-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 mb-4" style={{ borderBottom: '2px solid var(--theme-accent)' }}></div>
        </div>
      );
    }

    if (historyError) {
      return (
        <div className="text-center py-8">
          <p className="error-t">{historyError}</p>
        </div>
      );
    }

    if (!purchaseHistory || purchaseHistory.length === 0) {
      return (
        <div className="text-center py-8" style={{ color: 'var(--theme-muted)' }}>
          <p>No purchase history found for this seller.</p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto shadow-sm rounded-lg">
        <table className="min-w-full" style={{ borderCollapse: 'collapse' }}>
          <thead style={{ background: 'var(--theme-surface-alt)' }}>
            <tr>
              <th className="section-label-t px-3 py-2 text-left">Purchase Date</th>
              <th className="section-label-t px-3 py-2 text-left">Number Plate</th>
              <th className="section-label-t px-3 py-2 text-left">Make</th>
              <th className="section-label-t px-3 py-2 text-left">Model</th>
              <th className="section-label-t px-3 py-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {purchaseHistory.map((item) => (
              <tr key={item.DealAgreementNo} style={{ borderTop: '1px solid var(--theme-line)' }}>
                <td className="px-3 py-2 whitespace-nowrap text-sm tabular-nums" style={{ color: 'var(--theme-fg)' }}>
                  {item.PurchaseDate ? new Date(item.PurchaseDate).toLocaleDateString() : 'N/A'}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm" style={{ color: 'var(--theme-fg)' }}>
                  {item.NumberPlate || 'N/A'}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm" style={{ color: 'var(--theme-fg)' }}>
                  {item.Make}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm" style={{ color: 'var(--theme-fg)' }}>
                  {item.Model}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm">
                  <span className={`pill-t ${item.Status === 'Inventory' ? 'pill-t-muted' : 'pill-t-success'}`}>
                    {item.Status==="Inventory" ? "In Inventory" :
                      item.Status === "Cash" ? "Sold Cash" :
                      item.Status === "Credit" ? "Sold On Credit" :
                      item.Status === "CreditCleared" ? "Credit Cleared" : item.Status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
      {/* Main Dialog */}
      <Dialog.Root open={true} onOpenChange={() => showModal({ visible: false })} container={document.body}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-40" />
          <Theme>
            <Dialog.Content
              className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 overflow-hidden w-full max-w-3xl"
              style={{ transform: 'translate(-50%, -50%)', backfaceVisibility: 'hidden', background: 'var(--theme-surface)', borderRadius: 'var(--theme-radius-lg)', boxShadow: 'var(--theme-shadow-dialog)', padding: 0 }}
              onPointerDownOutside={(e) => e.preventDefault()}
              onEscapeKeyDown={(e) => e.preventDefault()}
            >
              <RadixModalHeader dialog={Dialog} icon={Store} title={getModalTitle()} onClose={handleClose} disabled={pendingSubmission} />

              <div className="px-4 pb-2 pt-1">
              {formMode !== "add" && (
                <FormModeCtx.Provider value={formMode}>
                <Tabs onSelect={index => setActiveTab(index)} selectedIndex={activeTab} className="mb-2">
                  <TabList className="formtabs flex overflow-x-auto" style={{ borderBottom: '1px solid var(--theme-line)', margin: 0, padding: 0 }}>
                    <Tab
                      className="formtab-btn"
                      selectedClassName="formtab-btn-active"
                    >
                      Seller Details
                    </Tab>
                    <Tab
                      className="formtab-btn"
                      selectedClassName="formtab-btn-active"
                    >
                      Purchase History
                    </Tab>
                    <Tab
                      className="formtab-btn"
                      selectedClassName="formtab-btn-active"
                    >
                      Messages
                    </Tab>
                  </TabList>

                  <div className="h-[300px] overflow-y-auto pt-2">
                    <TabPanel>
                      {/* Seller Details Form */}
                      <form className="pt-1" onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                          <div className="flex items-center gap-3 col-span-2">
                            <label className="label-t">Type:</label>
                            <input
                              type="radio"
                              name="IsCompany"
                              id="true"
                              checked={formData.IsCompany}
                              onChange={toggleCheckbox}
                              onBlur={handleBlur}
                              disabled={formMode === "fetch"}
                              style={{ accentColor: 'var(--theme-accent)' }}
                            />
                            <label htmlFor="true" className="mr-4" style={{ color: 'var(--theme-fg)' }}>Company</label>
                            <input
                              type="radio"
                              name="IsCompany"
                              id="false"
                              checked={!formData.IsCompany}
                              onChange={toggleCheckbox}
                              onBlur={handleBlur}
                              disabled={formMode === "fetch"}
                              style={{ accentColor: 'var(--theme-accent)' }}
                            />
                            <label htmlFor="false" style={{ color: 'var(--theme-fg)' }}>Individual</label>
                          </div>

                          <FormField
                            label="Company Name"
                            name="CompanyName"
                            value={formData.CompanyName || ""}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.CompanyName}
                            disabled={formMode === "fetch"}
                          />

                          <FormField
                            label="First Name"
                            name="FirstName"
                            value={formData.FirstName || ""}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.FirstName}
                            disabled={formMode === "fetch"}
                          />

                          <FormField
                            label="Last Name"
                            name="LastName"
                            value={formData.LastName || ""}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.LastName}
                            disabled={formMode === "fetch"}
                          />

                          <div className="flex flex-col">
                            <label className="mb-0.5 label-t">ID Type:</label>
                            <select
                              name="IDType"
                              value={formData.IDType || ""}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              className="input-t"
                              disabled={formMode === "fetch"}
                            >
                              {fields["IDType"].list.map((val) => (
                                <option value={val} key={val}>
                                  {val}
                                </option>
                              ))}
                            </select>
                            {errors.IDType && (
                              <span className="error-t mt-1">{errors.IDType}</span>
                            )}
                          </div>

                          <FormField
                            label="ID Number"
                            name="IDNumber"
                            value={formData.IDNumber || ""}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.IDNumber}
                            disabled={formMode === "fetch"}
                          />

                          <FormField
                            label="Address"
                            name="Address"
                            value={formData.Address || ""}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.Address}
                            disabled={formMode === "fetch"}
                          />

                          <FormField
                            label="Phone 1"
                            name="Phone1"
                            value={formData.Phone1 || ""}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.Phone1}
                            disabled={formMode === "fetch"}
                          />

                          <FormField
                            label="Phone 2"
                            name="Phone2"
                            value={formData.Phone2 || ""}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.Phone2}
                            disabled={formMode === "fetch"}
                          />
                        </div>
                      </form>
                    </TabPanel>

                    <TabPanel>
                      {/* Purchase History */}
                      <div className="h-full">
                        {renderPurchaseHistory()}
                      </div>
                    </TabPanel>

                    <TabPanel>
                      {/* Every SMS sent to this contact, keyed on their phone
                          number rather than their record id — the only
                          identifier customers, sellers and brokers share, so one
                          component serves all three modals. */}
                      <div className="h-full">
                        {formData.Phone1 ? (
                          <RecipientMessageHistory
                            phone={formData.Phone1}
                            name={[formData.FirstName, formData.LastName].filter(Boolean).join(' ')}
                          />
                        ) : (
                          <p className="text-sm text-gray-500 p-2">
                            No phone number on record, so no messages can have been sent.
                          </p>
                        )}
                      </div>
                    </TabPanel>
                  </div>
                </Tabs>
                </FormModeCtx.Provider>
              )}

              {formMode === "add" && (
                <div className="overflow-y-auto pt-2">
                  <form className="pt-1" onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                      <div className="flex items-center gap-3 col-span-2">
                        <label className="label-t">Type:</label>
                        <input
                          type="radio"
                          name="IsCompany"
                          id="true"
                          checked={formData.IsCompany}
                          onChange={toggleCheckbox}
                          onBlur={handleBlur}
                          disabled={formMode === "fetch"}
                          style={{ accentColor: 'var(--theme-accent)' }}
                        />
                        <label htmlFor="true" className="mr-4" style={{ color: 'var(--theme-fg)' }}>Company</label>
                        <input
                          type="radio"
                          name="IsCompany"
                          id="false"
                          checked={!formData.IsCompany}
                          onChange={toggleCheckbox}
                          onBlur={handleBlur}
                          disabled={formMode === "fetch"}
                          style={{ accentColor: 'var(--theme-accent)' }}
                        />
                        <label htmlFor="false" style={{ color: 'var(--theme-fg)' }}>Individual</label>
                      </div>

                      <FormField
                        label="Company Name"
                        name="CompanyName"
                        value={formData.CompanyName || ""}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={errors.CompanyName}
                      />

                      <FormField
                        label="First Name"
                        name="FirstName"
                        value={formData.FirstName || ""}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={errors.FirstName}
                      />

                      <FormField
                        label="Last Name"
                        name="LastName"
                        value={formData.LastName || ""}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={errors.LastName}
                      />

                      <div className="flex flex-col">
                        <label className="mb-0.5 label-t">ID Type:</label>
                        <select
                          name="IDType"
                          value={formData.IDType || ""}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className="input-t"
                        >
                          {fields["IDType"].list.map((val) => (
                            <option value={val} key={val}>
                              {val}
                            </option>
                          ))}
                        </select>
                        {errors.IDType && (
                          <span className="error-t mt-1">{errors.IDType}</span>
                        )}
                      </div>

                      <FormField
                        label="ID Number"
                        name="IDNumber"
                        value={formData.IDNumber || ""}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={errors.IDNumber}
                      />

                      <FormField
                        label="Address"
                        name="Address"
                        value={formData.Address || ""}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={errors.Address}
                      />

                      <FormField
                        label="Phone 1"
                        name="Phone1"
                        value={formData.Phone1 || ""}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={errors.Phone1}
                      />

                      <FormField
                        label="Phone 2"
                        name="Phone2"
                        value={formData.Phone2 || ""}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={errors.Phone2}
                      />
                    </div>
                  </form>

                </div>
              )}

              </div>

              <div className="modal-shell-footer">
                {formMode === "edit" && can('crm.delete') && (
                  <Button
                    color="red"
                    variant="soft"
                    size="2"
                    radius="medium"
                    style={{ marginRight: "auto" }}
                    onClick={handleDeleteSeller}
                  >
                    <DeleteIcon size={ICON_SIZE} />
                    Delete
                  </Button>
                )}
                <Button
                  variant="soft"
                  color="gray"
                  radius="medium"
                  onClick={handleClose}
                  disabled={pendingSubmission}
                >
                  <CancelIcon size={ICON_SIZE} />
                  Close
                </Button>
                {formMode === "add" && (
                  <Button
                    variant="solid"
                    radius="medium"
                    type="submit"
                    onClick={handleSubmit}
                    disabled={pendingSubmission}
                  >
                    <SaveIcon size={ICON_SIZE} />
                    {pendingSubmission ? "Checking..." : "Register"}
                  </Button>
                )}
              </div>
            </Dialog.Content>
          </Theme>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Duplicate Warning Dialog */}
      <Dialog.Root
        open={showDuplicateDialog}
        onOpenChange={(open) => {
          if (!open) handleDuplicateDialogClose(false);
        }}
        container={document.body}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <Theme>
            <Dialog.Content
              className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 overflow-hidden w-full max-w-2xl"
              style={{ transform: 'translate(-50%, -50%)', backfaceVisibility: 'hidden', background: 'var(--theme-surface)', borderRadius: 'var(--theme-radius-lg)', boxShadow: 'var(--theme-shadow-dialog)', padding: 0 }}
              onPointerDownOutside={(e) => { e.preventDefault(); }}
              onEscapeKeyDown={(e) => { e.preventDefault(); }}
            >
              <RadixModalHeader dialog={Dialog} icon={Copy} title="Possible Duplicate Seller" showClose={false} />

              <div className="modal-body-padded">
                <p style={{ color: 'var(--theme-fg)', marginBottom: 12 }}>
                  We found {duplicates.length} similar seller{duplicates.length > 1 ? 's' : ''} in the database:
                </p>

                <div className="max-h-60 overflow-y-auto rounded-lg p-3" style={{ background: 'var(--theme-surface-alt)' }}>
                  {duplicates.map((seller) => (
                    <div key={seller.SellerID} className="p-3 mb-2 rounded" style={{ background: 'var(--theme-surface)', border: '1px solid var(--theme-line)' }}>
                      <div className="font-medium" style={{ color: 'var(--theme-fg)' }}>
                        {formatSellerDisplay(seller)}
                      </div>
                      <div className="text-sm mt-1" style={{ color: 'var(--theme-muted)' }}>
                        {seller.Phone1 && `Phone: ${seller.Phone1}`}
                        {seller.Phone2 && ` | ${seller.Phone2}`}
                      </div>
                      <div className="mt-1 hint-t">
                        Seller ID: {seller.SellerID}
                      </div>
                    </div>
                  ))}
                </div>

                <p style={{ color: 'var(--theme-fg)', marginTop: 16, marginBottom: 0 }}>
                  Are you sure you want to add this as a new seller? If this person already exists, please cancel and select the existing seller instead.
                </p>
              </div>

              <div className="modal-shell-footer">
                <Button
                  variant="soft"
                  color="gray"
                  radius="medium"
                  onClick={() => { handleDuplicateDialogClose(false); }}
                ><CancelIcon size={ICON_SIZE} />
                  Cancel
                </Button>
                <Button
                  variant="solid"
                  radius="medium"
                  onClick={() => { handleDuplicateDialogClose(true); }}
                ><AddIcon size={ICON_SIZE} />
                  Add Anyway
                </Button>
              </div>
            </Dialog.Content>
          </Theme>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
};

function FormField({ label, name, value, onChange, onBlur, error, disabled = false }) {
  const formMode = useContext(FormModeCtx);
  const [editing, setEditing] = useState(false);
  const locked = formMode === "edit" && !editing && !disabled;
  return (
    <div className="flex flex-col">
      <label className="mb-0.5 label-t" htmlFor={name}>{label}</label>
      {locked ? (
        <div
          className="flex items-center gap-1 border px-3 py-1 rounded-md cursor-default min-h-[30px] overflow-hidden"
          style={{ background: 'var(--theme-surface-alt)', borderColor: 'var(--theme-line)' }}
          title={value ? `${value}\n\nClick the pencil icon to edit` : "Click the pencil icon to edit"}
        >
          <span className="flex-1 text-sm truncate" style={{ color: 'var(--theme-fg)' }}>{value || <span style={{ color: 'var(--theme-muted)', fontStyle: 'italic' }}>—</span>}</span>
          <button type="button" onClick={() => setEditing(true)}
            className="shrink-0 transition-colors btn-t-ghost" style={{ padding: '0 2px' }}>
            <EditIcon size={ICON_SIZE} />
          </button>
        </div>
      ) : (
        <input
          autoFocus={formMode === "edit" && editing}
          id={name}
          name={name}
          value={value}
          onChange={onChange}
          onBlur={(e) => { setEditing(false); onBlur(e); }}
          className={`input-t${error ? ' is-invalid' : ''}${disabled ? ' opacity-60' : ''}`}
          style={disabled ? { background: 'var(--theme-surface-alt)', cursor: 'not-allowed' } : undefined}
          disabled={disabled}
        />
      )}
      {error && <span className="error-t mt-1">{error}</span>}
    </div>
  );
}

export default AddEditSeller;
